import { ResponseResultCode } from "app/model/response-api";
import { BillService } from "app/services/cb/BillService";
import { numberFormatter } from "app/utils/stringUtil";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import React, {useEffect, useState} from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import { BillListHeaderDataModel } from "app/model/cb/billModel";
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";

const defaultChartSeries: Highcharts.SeriesOptionsType = {
  type: 'column',
  name: '구매내역',
  data: [0, 0, 0],
  showInLegend: false,
  colorByPoint: true,
};

const defaultOption: Highcharts.Options = {
  chart: {
    type: 'column',
    height: 280,
    spacingTop: 0,
    spacingBottom: 0,
    marginBottom: 40,
    marginTop: 40,
  },
  plotOptions: {
    column: {
      borderWidth: 0,
      pointWidth: 30,
      borderRadius: '50%'
    }
  },
  tooltip: {
    useHTML: true,
    formatter(this: any) {
      return `
        <div>
          <span style="color:${this.color};">●&nbsp;</span>
          <span style="font-weight:500;">${this.x}:&nbsp;</span>
          <span style="font-weight:700;">${numberFormatter(this.y, 1)}</span> 원
        </div>
      `;
    },
  },
  legend: { enabled: false },
  xAxis: {
    categories: ['청구 금액', '납부 금액', '미납 금액'],
    title: { text: '' },
    min: 0,
    crosshair: true,
    lineColor: '#E1E1E1',
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
    },
  },
  yAxis: {
    title: {
      text: '(만원)',
      align: 'high',
      rotation: 0,
      offset: 0,
      x: -3,
      y: -10,
      style: {
        color: '#444',
        fontSize: '0.65rem',
      },
    },
    min: 0,
    softMax: 10,
    lineColor: '#E1E1E1',
    labels: {
      x: -5,
      y: 10,
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
      formatter: function (this: any) {
        return numberFormatter(this.value / 10000, 1);
      },
    },
  },
  series: [defaultChartSeries],
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            spacingTop: 20,
          },
          xAxis: {
            labels: {
              style: {
                fontSize: '0.5rem',
              },
            },
          },
          yAxis: [
            {
              title: {
                y: -10,
                style: {
                  fontSize: '0.5rem',
                },
              },
              labels: {
                style: {
                  fontSize: '0.5rem',
                },
              },
            },
          ],
          legend: { enabled: false },
          tooltip: { enabled: false },
        },
      },
    ],
  },
};

export default function PchsDesc(props: Readonly<DashboardModuleProps>) {
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    colors: ['#4C96F9', '#00BB9F', '#F36363'],
    title: { text: "" },
    exporting: {enabled: false},
    credits: {enabled: false},
    tooltip: {shared: true, headerFormat: ''},
    accessibility: {enabled: false},
  });

  const { t } = useTranslation();
  const [option, setOption] = useState<any>(defaultOption);
  const [data, setData] = useState<BillListHeaderDataModel>(new BillListHeaderDataModel());

  useEffect(() => {
    BillService.getInstance().getBillDashboardBuy().then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        setData(response.data ?? new BillListHeaderDataModel());
      }
    });
  }, []);

  useEffect(() => {
    const handleResize = () => setDataToChartSeries(data);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setDataToChartSeries(data);
  }, [data]);

  function setDataToChartSeries(data: BillListHeaderDataModel) {
    setOption({
      ...defaultOption,
      series: [{
        ...defaultChartSeries,
        data: [data.reqSum, data.setlSum, data.nonSetlSum],
      }],
    });
  }

  return (
    <DashboardCard
      title="구매내역"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={false}
      dashboardConfig={props.dashboardConfig}
    >
      <div className="col-gap1">
        <div className="legend-box">
          <div className="l-guide">
            <p><i className="las la-clock"></i>{moment().format('YYYY.MM')} 기준</p>
          </div>
          <div className="l-lst-wrap">
            <div className="l-lst">
              <span className="icon1"></span>
              <div className="st1">
                <p className="txt">청구 금액
                  <strong> {data.reqCnt} <span>건</span></strong>
                </p>
                <p className="amount">
                  <CountUp end={data.reqSum} duration={0.5}>
                    {({ countUpRef }) => <strong ref={countUpRef} />}
                  </CountUp> {t('common.unit.won')}
                </p>
              </div>
            </div>
            <div className="l-lst">
              <span className="icon2"></span>
              <div className="st1">
                <p className="txt">납부 금액 <strong> {data.setlCnt} <span>건</span></strong></p>
                <p className="amount">
                  <CountUp end={data.setlSum} duration={0.5}>
                    {({ countUpRef }) => <strong ref={countUpRef} />}
                  </CountUp> {t('common.unit.won')}
                </p>
              </div>
            </div>
            <div className="l-lst">
              <span className="icon3"></span>
              <div className="st2">
                <p className="txt">미납 금액 <strong> {data.nonSetlCnt} <span>건</span></strong></p>
                <p className="amount">
                  <CountUp end={data.nonSetlSum} duration={0.5}>
                    {({ countUpRef }) => <strong ref={countUpRef} />}
                  </CountUp> {t('common.unit.won')}
                </p>
              </div>
            </div>
            <div className="l-lst">
              <span className="icon4"></span>
              <div className="st2">
                <p className="txt">총 누적 미납 금액</p>
                <p className="amount">
                  <CountUp end={data.allNonSetlSum} duration={0.5}>
                    {({ countUpRef }) => <strong ref={countUpRef} />}
                  </CountUp> {t('common.unit.won')}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="graph-area">
          <div className="chart">
            <HighchartsReact highcharts={Highcharts} options={option} />
          </div>
        </div>
      </div>
    </DashboardCard>
  )
}